import { ConversationAudioDirection } from '@polyai/common/types/resources/conversation';

import { doGet, doPost } from 'api/request';
import { LOGGER_TYPE } from 'api/types';
import type {
  ConversationData,
  ConversationsResponse,
  CreateConversationResponse,
  CreateMessageResponse,
} from './types';

export const NUM_CONVERSATIONS_PER_PAGE = 20;
const DEFAULT_LANGUAGE = process.env.NEXT_PUBLIC_DEFAULT_LANGUAGE;

export const getConversations = (
  accountId: string,
  projectId: string,
  query: string,
  limit: number = NUM_CONVERSATIONS_PER_PAGE,
) => {
  return doGet<ConversationsResponse>(
    `accounts/${accountId}/projects/${projectId}/conversations`,
    LOGGER_TYPE.CONVERSATIONS,
    {
      params: { limit },
      query,
      prefix: 'nebula',
    },
  );
};

export const getConversation = (
  conversationId: string,
  accountId: string,
  projectId: string,
  includeReview?: boolean,
) => {
  return doGet<ConversationData>(
    `accounts/${accountId}/projects/${projectId}/conversations/${conversationId}`,
    LOGGER_TYPE.CONVERSATIONS,
    {
      params: {
        ...(includeReview !== undefined && {
          include_review: includeReview,
        }),
      },
      prefix: 'nebula',
    },
  );
};

export const getConversationLatencyViz = (
  conversationId: string,
  accountId: string,
  projectId: string,
) => {
  return doGet<string>(
    `accounts/${accountId}/projects/${projectId}/conversations/${conversationId}/latency-viz`,
    LOGGER_TYPE.CONVERSATIONS,
    {
      returnRaw: true,
    },
  );
};

export const getSaferideApiLogJson = (
  accountId: string,
  projectId: string,
  conversationId: string,
) => {
  return doGet<Blob>(
    `accounts/${accountId}/projects/${projectId}/conversations/${conversationId}/api_response`,
    LOGGER_TYPE.CONVERSATIONS,
    {
      prefix: 'nebula',
      downloadingFile: true,
    },
  );
};

export const createConversation = ({
  accountId,
  projectId,
  currentPolicyVersion,
  variantId,
}: {
  accountId: string;
  projectId: string;
  currentPolicyVersion?: string;
  variantId?: string;
}) => {
  return doPost<CreateConversationResponse>(
    'conversations',
    LOGGER_TYPE.CONVERSATIONS,
    {
      account_id: accountId,
      project_id: projectId,
      client_env: 'sandbox',
      variant_id: variantId,
      version: currentPolicyVersion,
    },
  );
};

export const getConversationAudio = ({
  conversationId,
  redacted = true,
  exportAudio = true,
  direction = ConversationAudioDirection.COMBINED,
}: {
  conversationId: string;
  redacted?: boolean;
  exportAudio?: boolean;
  direction?: ConversationAudioDirection;
}) => {
  return doGet(
    `conversations/${conversationId}/audio`,
    LOGGER_TYPE.CONVERSATIONS,
    {
      params: {
        redacted,
        direction,
      },
      downloadingFile: exportAudio,
    },
  );
};

export const sendMessage = ({
  accountId,
  projectId,
  conversationId,
  text,
  currentPolicyVersion,
  language = DEFAULT_LANGUAGE,
}: {
  accountId: string;
  projectId: string;
  conversationId: string;
  text: string;
  currentPolicyVersion?: string;
  language: string | undefined;
}) => {
  return doPost<CreateMessageResponse>(
    `conversations/${conversationId}/turns`,
    LOGGER_TYPE.CONVERSATIONS,
    {
      account_id: accountId,
      project_id: projectId,
      client_env: 'sandbox',
      version: currentPolicyVersion,
      language: language,
      asr_lang_code: language,
      tts_lang_code: language,
      message: {
        text,
      },
      metadata: {},
    },
  );
};
